import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { PageHeader } from '@/shared/ui/PageHeader'
import { StatusBadge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { Input, Textarea } from '@/shared/ui/Input'
import { Spinner } from '@/shared/ui/Spinner'
import {
  Plus, ChevronLeft, ChevronRight, Calendar, CreditCard, Package as PackageIcon,
  User as UserIcon, Eye, RotateCcw, FileText,
} from 'lucide-react'
import { cn, formatCurrency, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import { toast } from '@/store/useToastStore'
import {
  useRefunds, useRefundedList, useCreateRefund,
  useProcessRefund, useUpdateRefundRequestStatus,
} from '@/api/hooks'
import { usePagination } from '@/shared/hooks'
import type { Refund } from '@/shared/types'

// ── Status enum (old fetched it from get-refund-request-status-enum; values are
//    pending / approved / rejected, matching row.status & STATUS_BADGE_STYLES) ──
const REFUND_STATUS_OPTIONS = [
  { value: 'pending' },
  { value: 'approved' },
  { value: 'rejected' },
]

const TABS = [
  { value: 'requests' },
  { value: 'refunded' },
  { value: 'rejected' },
] as const
type TabValue = (typeof TABS)[number]['value']

// ── Field extractors (rows nest the booking under `booking`) ───────────────────
const num = (v: unknown) => (v == null ? 0 : Number(v) || 0)

function studentName(r: Refund): string {
  return r.user?.name ?? (r.booking as any)?.user?.name ?? (r.user_id ? `User #${r.user_id}` : '—')
}
function driverName(r: Refund): string {
  return r.driver?.name ?? (r.booking as any)?.driver?.name ?? '—'
}
function packageName(r: Refund, t: TFunction): string {
  const pkg = (r.booking as any)?.package ?? r.package
  const tr = pkg?.package_translations
  return tr?.find((x: any) => x?.locale?.toLowerCase() === 'en')?.name ?? tr?.[0]?.name ?? pkg?.name ?? t('refundPage.unknownPackage')
}
function sessionLabel(r: Refund, t: TFunction): string {
  const pkg = (r.booking as any)?.package ?? r.package
  if (pkg?.is_unlimited) return t('refundPage.unlimitedSessions')
  return t('refundPage.sessionsCount', { n: pkg?.number_of_sessions ?? 0 })
}
function bookingStatusOf(r: Refund): string {
  return (r.booking as any)?.booking_status ?? r.booking_status ?? '—'
}
function paymentStatusOf(r: Refund): string {
  return (r.booking as any)?.payment_status ?? r.payment_status ?? '—'
}
function paymentMethodOf(r: Refund): string {
  return (r.booking as any)?.payment_method ?? r.payment_method ?? '—'
}
function requestBookingId(r: Refund): number | string {
  return (r.booking as any)?.id ?? r.booking_id ?? '—'
}
function refundedBookingId(r: Refund): number | string {
  return r.sessions?.[0]?.booking_id ?? r.booking_id ?? '—'
}
function fmtDateTime(v?: string | null): string {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    })
  } catch { return v }
}
function studentPhoto(r: Refund): string | undefined {
  return r.user?.photo_url ?? (r.booking as any)?.user?.photo_url
}

// ── Per-row refund action: status dropdown, or "Refund" button when approved ───
function RefundAction({ row }: { row: Refund }) {
  const { t } = useTranslation()
  const process = useProcessRefund()
  const updateStatus = useUpdateRefundRequestStatus()
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState<string>('')

  // Live status options (old dashboard fetches these from
  // get-refund-request-status-enum rather than hardcoding them). React Query
  // dedupes this across every row's RefundAction instance into one request.
  // Real shape is { status, message, values: [...] }, not `.data` — same
  // gotcha as the booking-status enum.
  const { data: statusEnum } = useQuery({
    queryKey: ['refund-status-enum'],
    queryFn: () => restClient.get(endpoints.enum.refundRequestStatus).then(r => r.data),
    staleTime: Infinity,
  })
  const liveOptions: any[] = Array.isArray((statusEnum as any)?.values) ? (statusEnum as any).values
    : Array.isArray((statusEnum as any)?.data) ? (statusEnum as any).data : []
  const statusOptions = liveOptions.length ? liveOptions : REFUND_STATUS_OPTIONS

  const bookingId = row.booking_id ?? (row.booking as any)?.id
  const remaining = num(row.remaining_amount_to_refund)
  const isApproved = String(row.status).toLowerCase() === 'approved'

  const onStatusChange = (status: string) => {
    if (status === row.status) return
    // Real payload contract (confirmed via live 400s from the backend): the
    // request struct requires `RefundRequestID` (row.id, NOT booking id) and
    // `Action` — and Action fails Go's `oneof` validator when given the raw
    // status noun ("approved"/"rejected"). It wants the verb form instead.
    const ACTION_BY_STATUS: Record<string, string> = { approved: 'approve', rejected: 'reject', pending: 'pending' }
    const action = ACTION_BY_STATUS[status.toLowerCase()] ?? status
    updateStatus.mutate(
      { refund_request_id: row.id, action },
      {
        onSuccess: () => toast.success(t('refundPage.toasts.statusUpdated')),
        onError: (e: any) => toast.error(e?.response?.data?.message ?? t('refundPage.toasts.statusUpdateFailed')),
      },
    )
  }

  const onAmountChange = (raw: string) => {
    const v = raw.trim()
    if (!/^\d*\.?\d*$/.test(v)) return
    if (v === '') return setAmount('')
    let n = Number(v)
    if (n < 0) n = 0
    if (n > remaining) n = remaining
    setAmount(String(n))
  }

  const submitRefund = () => {
    const n = Number(amount)
    if (isNaN(n) || n <= 0) { toast.error(t('refundPage.toasts.invalidAmount')); return }
    process.mutate(
      { booking_id: String(bookingId), amount_refunded: amount },
      {
        onSuccess: () => { toast.success(t('refundPage.toasts.statusUpdated')); setOpen(false); setAmount('') },
        onError: (e: any) => toast.error(e?.response?.data?.message ?? t('refundPage.toasts.processFailed')),
      },
    )
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex-1">
      {isApproved ? (
        <Button variant="primary" size="sm" className="w-full justify-center" onClick={() => setOpen(true)} title={t('refundPage.action.processTitle')}>
          <RotateCcw size={12} /> {t('refundPage.action.processRefund')}
        </Button>
      ) : (
        <select
          value={String(row.status)}
          onChange={(e) => onStatusChange(e.target.value)}
          disabled={updateStatus.isPending}
          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
        >
          {statusOptions.map((o: any) => (
            <option key={o.value} value={o.value}>{o.name ?? t(`refundPage.statusOptions.${o.value}`)}</option>
          ))}
        </select>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={t('refundPage.processModal.title')} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('refundPage.processModal.remaining', { id: bookingId })} <span className="font-semibold text-purple-700">{formatCurrency(remaining)}</span>
          </p>
          <Input
            label={t('refundPage.processModal.amountLabel')}
            type="number"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            placeholder={t('refundPage.processModal.amountPlaceholder')}
            helperText={t('refundPage.processModal.maxHelper', { amount: formatCurrency(remaining) })}
          />
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('refundPage.processModal.cancel')}</button>
            <Button onClick={submitRefund} loading={process.isPending} disabled={amount === '' || Number(amount) <= 0}>
              {t('refundPage.processModal.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── Small building blocks ──────────────────────────────────────────────────────
function InfoRow({ icon, label, value, valueCls }: { icon: React.ReactNode; label: string; value: React.ReactNode; valueCls?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="flex items-center gap-1.5 text-gray-400">{icon}{label}</span>
      <span className={cn('font-semibold text-gray-700 text-right truncate', valueCls)}>{value}</span>
    </div>
  )
}

// ── Refund card (Requests / Rejected tabs) ─────────────────────────────────────
function RefundRequestCard({ row, onView, onUser, onBooking, showAction }: {
  row: Refund; onView: () => void; onUser: () => void; onBooking: () => void; showAction: boolean
}) {
  const { t } = useTranslation()
  const name = studentName(row)
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-start justify-between gap-3 border-b border-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <img src={studentPhoto(row) || DUMMY_AVATAR} alt={name} onError={onAvatarError} className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            <button onClick={(e) => { e.stopPropagation(); onUser() }} className="block text-sm font-bold text-gray-900 truncate hover:text-brand-600 hover:underline text-left">{name}</button>
            <button onClick={(e) => { e.stopPropagation(); onBooking() }} className="text-[13px] text-brand-600 hover:underline font-mono">{t('refundPage.booking')} #{requestBookingId(row)}</button>
          </div>
        </div>
        <StatusBadge status={String(row.status)} />
      </div>

      {/* Package + statuses */}
      <div className="px-4 py-3 space-y-2 border-b border-gray-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-6 h-6 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0"><PackageIcon size={11} className="text-brand-600" /></div>
            <span className="text-xs font-semibold text-gray-800 truncate">{packageName(row, t)}</span>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[12px] font-semibold bg-brand-50 text-brand-700 flex-shrink-0">{sessionLabel(row, t)}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={String(bookingStatusOf(row))} />
          <StatusBadge status={String(paymentStatusOf(row))} />
        </div>
      </div>

      {/* Amounts */}
      <div className="px-4 py-3 grid grid-cols-2 gap-2 border-b border-gray-50">
        <div className="rounded-xl bg-gray-50 p-2.5">
          <div className="text-[11px] uppercase font-semibold text-gray-400">{t('refundPage.card.amountSanctioned')}</div>
          <div className="text-sm font-black text-gray-800">{formatCurrency(num(row.refund_amount_sanctioned))}</div>
        </div>
        <div className="rounded-xl bg-purple-50 p-2.5">
          <div className="text-[11px] uppercase font-semibold text-purple-400">{t('refundPage.card.amountToRefund')}</div>
          <div className="text-sm font-black text-purple-700">{formatCurrency(num(row.remaining_amount_to_refund))}</div>
        </div>
      </div>

      {/* Meta */}
      <div className="px-4 py-3 space-y-1.5 flex-1">
        <InfoRow icon={<CreditCard size={11} />} label={t('refundPage.card.payment')} value={paymentMethodOf(row)} />
        <InfoRow icon={<FileText size={11} />} label={t('refundPage.card.reason')} value={<span title={row.reason ?? ''}>{row.reason || '—'}</span>} />
        <InfoRow icon={<Calendar size={11} />} label={t('refundPage.card.created')} value={fmtDateTime(row.created_at)} />
        <InfoRow icon={<RotateCcw size={11} />} label={t('refundPage.card.updated')} value={fmtDateTime(row.updated_at)} />
      </div>

      {/* Footer actions */}
      <div className="px-4 pb-4 flex items-center gap-2">
        {showAction && <RefundAction row={row} />}
        <button onClick={(e) => { e.stopPropagation(); onView() }} className="px-3 py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold flex items-center gap-1 hover:bg-brand-50 flex-shrink-0">
          <Eye size={12} /> {t('refundPage.card.breakdown')}
        </button>
      </div>
    </div>
  )
}

// ── Refunded card (Refunded tab) ───────────────────────────────────────────────
function RefundedCard({ row, onView, onUser }: { row: Refund; onView: () => void; onUser: () => void }) {
  const { t } = useTranslation()
  const name = studentName(row)
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className="p-4 flex items-start justify-between gap-3 border-b border-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <img src={studentPhoto(row) || DUMMY_AVATAR} alt={name} onError={onAvatarError} className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            <button onClick={(e) => { e.stopPropagation(); onUser() }} className="block text-sm font-bold text-gray-900 truncate hover:text-brand-600 hover:underline text-left">{name}</button>
            <span className="text-[13px] text-gray-400 font-mono">{t('refundPage.booking')} #{refundedBookingId(row)}</span>
          </div>
        </div>
        <span className="px-2 py-0.5 rounded-full text-[12px] font-bold bg-purple-100 text-purple-700 flex-shrink-0">{t('refundPage.card.refunded')}</span>
      </div>

      <div className="px-4 py-3 space-y-2 border-b border-gray-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-6 h-6 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0"><PackageIcon size={11} className="text-brand-600" /></div>
            <span className="text-xs font-semibold text-gray-800 truncate">{packageName(row, t)}</span>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[12px] font-semibold bg-brand-50 text-brand-700 flex-shrink-0">{sessionLabel(row, t)}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={String(bookingStatusOf(row))} />
          <StatusBadge status={String(paymentStatusOf(row))} />
        </div>
      </div>

      <div className="px-4 py-3 space-y-1.5 flex-1">
        <InfoRow icon={<UserIcon size={11} />} label={t('refundPage.card.driver')} value={driverName(row)} />
        <InfoRow icon={<CreditCard size={11} />} label={t('refundPage.card.price')} value={formatCurrency(num(row.sub_total))} valueCls="text-brand-600" />
        <InfoRow icon={<CreditCard size={11} />} label={t('refundPage.card.payment')} value={paymentMethodOf(row)} />
        <InfoRow icon={<FileText size={11} />} label={t('refundPage.card.reason')} value={<span title={row.refund_reason ?? ''}>{row.refund_reason || '—'}</span>} />
        <InfoRow icon={<Calendar size={11} />} label={t('refundPage.card.created')} value={fmtDateTime(row.created_at)} />
      </div>

      <div className="px-4 pb-4">
        <button onClick={(e) => { e.stopPropagation(); onView() }} className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50">
          <Eye size={12} /> {t('refundPage.card.viewBreakdown')}
        </button>
      </div>
    </div>
  )
}

// ── Paginator ──────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage }: { page: number; lastPage: number; total: number; perPage: number; onPage: (p: number) => void }) {
  const { t } = useTranslation()
  const nums = (() => {
    let s = Math.max(1, page - 2); if (s + 4 > lastPage) s = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => s + i).filter((p) => p <= lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <span className="text-xs text-gray-500">{t('refundPage.paginator.range', { start: (page - 1) * perPage + 1, end: Math.min(page * perPage, total), total })}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronLeft size={14} className="rtl:rotate-180" /></button>
        {nums.map((p) => (
          <button key={p} onClick={() => onPage(p)} className={cn('w-8 h-8 rounded-lg border text-xs font-semibold', p === page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>{p}</button>
        ))}
        <button onClick={() => onPage(Math.min(lastPage, page + 1))} disabled={page >= lastPage} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronRight size={14} className="rtl:rotate-180" /></button>
      </div>
    </div>
  )
}

export function RefundPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')
  const base = isSchool ? '/school' : '/dashboard'

  const [tab, setTab] = useState<TabValue>('requests')
  // Default to Pending only — Approved requests already have their own tab, so
  // combining pending+approved by default made the two impossible to tell apart.
  const [statusFilter, setStatusFilter] = useState('pending') // requests tab: all | pending | approved
  const { pagination, setPagination } = usePagination({ page: 1, per_page: 9 })

  const resetPage = () => setPagination({ ...pagination, page: 1 })
  const goPage = (p: number) => setPagination({ ...pagination, page: p })

  // Queries — only the active tab fetches data
  const requests = useRefunds(
    tab === 'requests'
      ? { page: pagination.page, limit: pagination.per_page, status: statusFilter === 'all' ? ['pending', 'approved'] : statusFilter }
      : { page: 1, limit: 1, status: '__noop__' },
  )
  const rejected = useRefunds(
    tab === 'rejected'
      ? { page: pagination.page, limit: pagination.per_page, status: ['rejected'] }
      : { page: 1, limit: 1, status: '__noop__' },
  )
  // "Approved Requests" tab → request-list filtered by status[]=approved
  const refunded = useRefunds(
    tab === 'refunded'
      ? { page: pagination.page, limit: pagination.per_page, status: ['approved'] }
      : { page: 1, limit: 1, status: '__noop__' },
  )

  const active = tab === 'requests' ? requests : tab === 'rejected' ? rejected : refunded
  const resp: any = active.data
  // Response shape varies: { data: [...] } | { data: { data: [...] } } | [...]
  const rows: Refund[] = Array.isArray(resp?.data) ? resp.data
    : Array.isArray(resp?.data?.data) ? resp.data.data
    : Array.isArray(resp) ? resp
    : []
  const total = resp?.total ?? resp?.data?.total ?? rows.length
  const perPage = resp?.count_per_page ?? resp?.data?.count_per_page ?? pagination.per_page
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1)

  // Create refund request
  const createRefund = useCreateRefund()
  const [createOpen, setCreateOpen] = useState(false)
  const [newBookingId, setNewBookingId] = useState('')
  const [newReason, setNewReason] = useState('')

  const submitCreate = () => {
    if (!newBookingId.trim()) { toast.error(t('refundPage.toasts.bookingIdRequired')); return }
    createRefund.mutate(
      { booking_id: newBookingId.trim(), reason: newReason },
      {
        onSuccess: (res: any) => {
          toast.success(res?.message ?? t('refundPage.toasts.requestCreated'))
          setCreateOpen(false); setNewBookingId(''); setNewReason('')
        },
        onError: (e: any) => {
          const errs = e?.response?.data?.errors
          if (errs && typeof errs === 'object') {
            Object.values(errs).forEach((m: any) => toast.error(Array.isArray(m) ? m[0] : String(m)))
          } else {
            toast.error(e?.response?.data?.message ?? t('refundPage.toasts.createFailed'))
          }
        },
      },
    )
  }

  const detailsId = (r: Refund) => r.booking_id ?? (r.booking as any)?.id
  const goToDetails = (r: Refund) => {
    const bId = detailsId(r)
    if (bId && bId !== '—') navigate(`${base}/booking/refund/${bId}`)
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('refundPage.pageTitle')}
        breadcrumbs={[{ label: t('refundPage.breadcrumb.dashboard'), to: base }, { label: t('refundPage.breadcrumb.refund') }, { label: t('refundPage.breadcrumb.list') }]}
        action={<Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>{t('refundPage.createRequest')}</Button>}
      />

      {/* Tabs + status sub-filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map((tb) => (
            <button
              key={tb.value}
              onClick={() => { setTab(tb.value); resetPage() }}
              className={cn('px-4 py-2 rounded-xl text-xs font-bold border transition-colors',
                tab === tb.value ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600')}
            >{t(`refundPage.tabs.${tb.value}`)}</button>
          ))}
        </div>
        {tab === 'requests' && (
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); resetPage() }} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-32 focus:outline-none focus:ring-2 focus:ring-brand-400">
            <option value="pending">{t('refundPage.statusFilter.pending')}</option>
            <option value="approved">{t('refundPage.statusFilter.approved')}</option>
            <option value="all">{t('refundPage.statusFilter.all')}</option>
          </select>
        )}
      </div>

      {active.isError && <p className="text-sm text-red-500">{t('refundPage.loadFailed')}</p>}

      {/* Card grid */}
      {active.isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3"><RotateCcw size={24} className="text-gray-300" /></div>
          <p className="text-sm font-semibold text-gray-500">{t('refundPage.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {rows.map((row) => (
            <RefundRequestCard
              key={row.id}
              row={row}
              showAction={tab === 'requests' || tab === 'refunded'}
              onView={() => goToDetails(row)}
              onUser={() => row.user_id && navigate(`${base}/user/${row.user_id}`)}
              onBooking={() => { const b = (row.booking as any)?.id; if (b) navigate(`${base}/booking/${b}`) }}
            />
          ))}
        </div>
      )}

      {!active.isLoading && total > 0 && (
        <Paginator page={pagination.page} lastPage={lastPage} total={total} perPage={perPage} onPage={goPage} />
      )}

      {/* Create refund request modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('refundPage.createModal.title')} size="sm">
        <div className="space-y-4">
          <Input label={t('refundPage.createModal.bookingIdLabel')} value={newBookingId} onChange={(e) => setNewBookingId(e.target.value)} placeholder={t('refundPage.createModal.bookingIdPlaceholder')} />
          <Textarea label={t('refundPage.createModal.reasonLabel')} value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder={t('refundPage.createModal.reasonPlaceholder')} className="min-h-[84px]" />
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('refundPage.createModal.cancel')}</button>
            <Button onClick={submitCreate} loading={createRefund.isPending}>{t('refundPage.createModal.submit')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
